import React from "react";
import styles from "./styles.module.scss";
import Button from "../../../../components/common/ui-components/input/Button";
import { EnterpriseContactUs } from "../../../../api/landingpages";
import { cleverTapTrigger } from "../../../../tracking/segment";
import { regExpEmail } from "../../../../constants";
import Image from "@/components/UI/Image";
import UITextField from "../../../../components/common/ui-components/input/UITextField";
import UITextArea from "../../../../components/common/ui-components/input/UITextArea";
import UIDropdown from "../../../../components/common/ui-components/input/UIDropdown/UIDropdown";
import { DEVICE_TYPES, DEVICES } from "@/constants/enterprise";

interface FormData {
  company_name: string;
  name: string;
  email: string;
  phone: string;
  no_of_devices: string;
  device_type: string;
  message: string;
}

const Form: React.FC = () => {
  const [formData, setFormData] = React.useState<FormData>({
    company_name: "",
    name: "",
    email: "",
    phone: "",
    no_of_devices: "",
    device_type: "",
    message: "",
  });

  const [error, setError] = React.useState<any>({});
  const [btnLoading, setBtnLoading] = React.useState<boolean>(false);
  const [submitted, setSubmitted] = React.useState<boolean>(false);

  const handleInputChange = (value: string, name: string) => {
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));

    setError((prevError: any) => ({
      ...prevError,
      [name]: "",
    }));
  };

  const handleSubmit = () => {
    setBtnLoading(true);

    const newErrors: any = {};
    const data = { ...formData };

    const requiredFields: any = {
      company_name: "Please enter your company name",
      name: "Please enter your name",
      email: "Please enter your email address",
      phone: "Please enter your phone number",
      no_of_devices: "Please select number of devices",
      device_type: "Please select a device type",
    };

    Object.keys(requiredFields).forEach((field) => {
      if (!data[field] || !data[field].trim()) {
        newErrors[field] = requiredFields[field];
      }
    });

    if (!newErrors.email && !regExpEmail.test(data.email)) {
      newErrors.email = "Please enter a valid email address";
    }

    if (!newErrors.phone && !/^\d{10}$/.test(data.phone)) {
      newErrors.phone = "Phone number must be 10 digits";
    }

    if (Object.keys(newErrors).length > 0) {
      setError(newErrors);
      setBtnLoading(false);
      return;
    }

    EnterpriseContactUs(data)
      .then(() => {
        cleverTapTrigger("Get In Touch Enterprise Submitted", data);
        document.querySelector("#plan-selection")?.scrollIntoView({
              behavior: 'smooth'
          })
        setSubmitted(true);
      })
      .catch((err) => console.error("Error submitting form:", err))
      .finally(() => setBtnLoading(false));
  };

  return (
    <div className={styles.formContainer}>
      <h4 className={styles.formTitle}>Get in touch with us</h4>

      {!submitted ? (
        <form className={styles.form}>
          <div className={styles.Name}>
            <i></i>
            <UITextField
              placeholder="Company Name"
              name="company_name"
              id="company_name"
              onChange={(e) => handleInputChange(e.target.value, e.target.name)}
              value={formData.company_name}
              propsError={error["company_name"]}
              className={styles.formInput}
              label=""
            />
          </div>
          <div className={styles.Name}>
            <i></i>
            <UITextField
              placeholder="Your Name"
              name="name"
              id="name"
              onChange={(e) => handleInputChange(e.target.value, e.target.name)}
              value={formData.name}
              propsError={error["name"]}
              className={styles.formInput}
              label=""
            />
          </div>

          <div className={styles.email}>
            <i></i>
            <UITextField
              type="email"
              placeholder="Email Address"
              name="email"
              id="email"
              onChange={(e) => handleInputChange(e.target.value, e.target.name)}
              value={formData.email}
              propsError={error["email"]}
              className={styles.formInput}
              label=""
            />
          </div>

          <div className={styles.phone}>
            <i></i>
            <UITextField
              type="tel"
              placeholder="Phone Number"
              name="phone"
              id="phone"
              maxLength={10}
              onChange={(e) => handleInputChange(e.target.value, e.target.name)}
              value={formData.phone}
              propsError={error["phone"]}
              className={styles.formInput}
              label=""
            />
          </div>

          <div>
            <UIDropdown
              options={DEVICES}
              placeholder="Select number of devices"
              onChange={(e) => handleInputChange(e.value, "no_of_devices")}
              id="no_of_devices"
              selected_option={
                formData.no_of_devices
                  ? {
                      key: formData.no_of_devices,
                      text: formData.no_of_devices,
                      value: formData.no_of_devices,
                    }
                  : undefined
              }
              value={formData.no_of_devices}
              propsError={error["no_of_devices"]}
              className={styles.dropdownInput}
              label=""
            />
          </div>
          <div>
            <UIDropdown
              options={DEVICE_TYPES}
              placeholder="Select a device type"
              onChange={(e) => handleInputChange(e.value, "device_type")}
              id="device_type"
              selected_option={
                formData.device_type
                  ? {
                      key: formData.device_type,
                      text: formData.device_type,
                      value: formData.device_type,
                    }
                  : undefined
              }
              value={formData.device_type}
              propsError={error["device_type"]}
              className={styles.dropdownInput}
              label=""
            />
          </div>
          <UITextArea
            label="Please describe your specific requirements, timeline, or any questions you have..."
            value={formData.message}
            onChange={(e) => handleInputChange(e.target.value, e.target.name)}
            id="message"
            propsError={error["message"]}
            className={styles.formInputArea}
          />

          <Button
            onClick={handleSubmit}
            className={styles.FormBtn}
            text="SUBMIT"
            loading={btnLoading}
            variant="green"
          />
        </form>
      ) : (
        <div className={styles.successfulSubmission}>
          <div className={styles.verified_image}>
            <Image
              src="/static/images/b2b/landingpage/verified.svg"
              alt="verified"
              height="48"
              width="48"
            />
          </div>
          <div className={styles.verified_title}>
            Thank you for reaching out!
          </div>
          <div className={styles.verified_message}>
            Our team will connect with you soon.
          </div>
        </div>
      )}
    </div>
  );
};

export default Form;
